/**
 * Playground tool-call trace — types + the two pure helpers the panel renders with.
 *
 * PORTED from the admin build (`admin-front/src/components/ConversationReplay/ToolCallList.tsx`,
 * shipped in `146f0b7`) rather than re-derived, so the two surfaces pair, format
 * and degrade IDENTICALLY. `front/` and `admin-front/` are separate npm apps with
 * no shared package, so duplication is the established convention here. If the
 * capture shape in `back/api/widget.py::_run_agent_in_background` changes, both
 * copies must follow.
 *
 * SOURCE OF TRUTH: `GET /api/widget/playground/conversation/{session_id}/trace`
 * (staff-authenticated). NEVER the customer chat response, and NEVER the widget
 * WebSocket — that socket has no authentication at all (it is identified by
 * `session_id` alone), so anything placed on it is readable by anyone holding
 * the id. Tool payloads carry real customer PII. That route is permanently ruled
 * out; do not revive it.
 */

/** One captured tool event, exactly as persisted by the agent runner:
 *    { kind: "call",   name, args: <object>,          timestamp }
 *    { kind: "result", name, response: <string|null>, timestamp }
 */
export interface ToolCallEvent {
  kind?: 'call' | 'result' | string;
  name?: string;
  /** Present on `call` — a real JSON object. */
  args?: unknown;
  /** Present on `result`. ⚠️ A JSON STRING truncated to 1000 chars at capture,
   *  so it can be cut MID-TOKEN and is frequently NOT valid JSON. */
  response?: string | null;
  timestamp?: string;
}

/**
 * Tool activity for ONE message.
 *
 * 🚨 `message_index` is an index into `conversations.messages[]` — the JOIN KEY.
 * The `entries` array is SPARSE: messages that made no tool calls are OMITTED by
 * the backend (`if not tc: continue`), so `entries` is NOT parallel to the
 * transcript. Never line these up by array position.
 */
export interface PlaygroundTraceEntry {
  message_index: number;
  role: string;
  timestamp?: string | null;
  response_time_ms?: number | null;
  tool_calls: ToolCallEvent[];
}

export interface PlaygroundTrace {
  session_id: string;
  entries: PlaygroundTraceEntry[];
}

/** A `call` matched with its `result`. */
export interface PairedToolCall {
  name: string;
  args?: unknown;
  response?: string | null;
  timestamp?: string;
  /** No matching result — the turn errored, was cut short, or the result was
   *  never captured. Surfaced explicitly rather than implied by an empty box. */
  pending: boolean;
}

/**
 * Pretty-print a captured payload. NEVER throws.
 *
 * 🚨 The whole reason this exists: a tool `response` is `json.dumps(...)[:1000]`
 * — TRUNCATED AT 1000 CHARS at capture time — so on any real payload it is cut
 * MID-TOKEN and is not valid JSON. A bare `JSON.parse` WILL throw on live data.
 * We try to parse and pretty-print; on failure we show the raw (truncated)
 * string, because a partial record still answers "what did the tool return",
 * which is the entire point of the panel. Blank or crash would both be worse.
 */
export function formatToolPayload(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '—';
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    // Cyclic / unserialisable — cannot happen over JSON, but throwing here
    // would take the whole panel down.
    return String(value);
  }
}

/**
 * Pair each `call` with the NEXT unconsumed `result` of the same name (FIFO) —
 * the same order the backend closes out pending calls in. A `result` with no
 * preceding `call` (possible if a turn was truncated mid-capture) is still
 * surfaced rather than silently dropped.
 */
export function pairToolCalls(toolCalls: ToolCallEvent[]): PairedToolCall[] {
  const calls: PairedToolCall[] = [];
  const openByName = new Map<string, number[]>();

  for (const tc of toolCalls || []) {
    if (!tc || typeof tc !== 'object' || !tc.name) continue;
    if (tc.kind === 'call') {
      const idx = calls.length;
      calls.push({ name: tc.name, args: tc.args, timestamp: tc.timestamp, pending: true });
      const queue = openByName.get(tc.name) || [];
      queue.push(idx);
      openByName.set(tc.name, queue);
    } else if (tc.kind === 'result') {
      const queue = openByName.get(tc.name);
      const idx = queue && queue.length > 0 ? queue.shift() : undefined;
      if (idx !== undefined) {
        calls[idx].response = tc.response ?? null;
        calls[idx].pending = false;
      } else {
        calls.push({
          name: tc.name,
          response: tc.response ?? null,
          timestamp: tc.timestamp,
          pending: false,
        });
      }
    }
  }
  return calls;
}
